import { useCallback } from "react";
import { useFormStore } from "../store/formStore";
import { useUiStore } from "../store/uiStore";
import { getApi } from "../api";
import { trackWarning } from "../telemetry/appInsights";
import type { AffiliatedGroup, IntakeFormData, OwnershipAssignment } from "../types";
import { isOwnerEligibilityVisible } from "./step7Rules";

/**
 * Predictive guard: given a projected ownership-assignments list (what the
 * store would hold *after* the pending mutation), return true when the change
 * would hide the Owner Eligibility Verification section AND the user has already
 * uploaded owner-eligibility docs. That's the only case where a confirmation
 * toast is warranted — everything else runs immediately.
 */
function wouldOrphanOwnerDocs(newAssignments: OwnershipAssignment[], data: IntakeFormData): boolean {
  if (data.ownerEligibilityDocs.length === 0) return false;
  return !isOwnerEligibilityVisible(newAssignments);
}

function deleteOrphanedOwnerDocs() {
  const api = getApi();
  const state = useFormStore.getState();
  for (const doc of state.data.ownerEligibilityDocs) {
    void api
      .deleteFile(doc.fileId)
      .catch(() => trackWarning("useDocumentGuard: owner doc delete failed", { fileId: doc.fileId }));
  }
  const count = state.data.ownerEligibilityDocs.length;
  for (let i = count - 1; i >= 0; i--) {
    state.removeOwnerEligibilityDoc(i);
  }
}

function showConfirmation(
  onConfirm: () => void,
  addToast: ReturnType<typeof useUiStore.getState>["addToast"],
) {
  addToast(
    "This change will remove your previously uploaded documents on the Documents & Employees page. Would you like to continue?",
    "warning",
    0,
    [
      { label: "Cancel", onClick: () => {} },
      {
        label: "Continue",
        primary: true,
        onClick: () => {
          onConfirm();
          deleteOrphanedOwnerDocs();
        },
      },
    ],
    false,
  );
}

/**
 * Returns guarded wrappers around the mutations that can hide the Owner
 * Eligibility section:
 *   - removing an owner (cascades → drops their assignments)
 *   - updating an ownership assignment (enrolling/W-2 changes)
 *   - removing an ownership assignment
 *   - updating an affiliated group (flipping Covered Yes→No cascades → drops its assignments)
 *   - removing an affiliated group (cascades → drops its assignments)
 * Roster edits (name-only) can't hide the section, so there's no guarded owner
 * update — callers use the plain store action.
 */
export function useDocumentGuard() {
  const addToast = useUiStore((s) => s.addToast);

  const guardedRemoveOwner = useCallback(
    (index: number) => {
      const { data, removeOwner } = useFormStore.getState();
      const removed = data.owners[index];
      const projected = removed
        ? data.ownershipAssignments.filter((a) => a.ownerId !== removed.id)
        : data.ownershipAssignments;
      if (!wouldOrphanOwnerDocs(projected, data)) {
        removeOwner(index);
        return;
      }
      showConfirmation(() => removeOwner(index), addToast);
    },
    [addToast],
  );

  const guardedUpdateOwnershipAssignment = useCallback(
    (id: string, patch: Partial<OwnershipAssignment>) => {
      const { data, updateOwnershipAssignment } = useFormStore.getState();
      const projected = data.ownershipAssignments.map((a) => (a.id === id ? { ...a, ...patch } : a));
      if (!wouldOrphanOwnerDocs(projected, data)) {
        updateOwnershipAssignment(id, patch);
        return;
      }
      showConfirmation(() => updateOwnershipAssignment(id, patch), addToast);
    },
    [addToast],
  );

  const guardedRemoveOwnershipAssignment = useCallback(
    (id: string) => {
      const { data, removeOwnershipAssignment } = useFormStore.getState();
      const projected = data.ownershipAssignments.filter((a) => a.id !== id);
      if (!wouldOrphanOwnerDocs(projected, data)) {
        removeOwnershipAssignment(id);
        return;
      }
      showConfirmation(() => removeOwnershipAssignment(id), addToast);
    },
    [addToast],
  );

  const guardedUpdateAffiliatedGroup = useCallback(
    (index: number, group: AffiliatedGroup) => {
      const { data, updateAffiliatedGroup } = useFormStore.getState();
      const prev = data.affiliatedGroups[index];
      const droppedCoverage = prev?.covered === true && group.covered !== true;
      const projected = droppedCoverage
        ? data.ownershipAssignments.filter((a) => a.entityId !== group.id)
        : data.ownershipAssignments;
      if (!wouldOrphanOwnerDocs(projected, data)) {
        updateAffiliatedGroup(index, group);
        return;
      }
      showConfirmation(() => updateAffiliatedGroup(index, group), addToast);
    },
    [addToast],
  );

  const guardedRemoveAffiliatedGroup = useCallback(
    (index: number) => {
      const { data, removeAffiliatedGroup } = useFormStore.getState();
      const removed = data.affiliatedGroups[index];
      const projected = removed
        ? data.ownershipAssignments.filter((a) => a.entityId !== removed.id)
        : data.ownershipAssignments;
      if (!wouldOrphanOwnerDocs(projected, data)) {
        removeAffiliatedGroup(index);
        return;
      }
      showConfirmation(() => removeAffiliatedGroup(index), addToast);
    },
    [addToast],
  );

  return {
    guardedRemoveOwner,
    guardedUpdateOwnershipAssignment,
    guardedRemoveOwnershipAssignment,
    guardedUpdateAffiliatedGroup,
    guardedRemoveAffiliatedGroup,
  };
}
